import React, { useContext } from 'react';

import { ReactComponent as PlusIcon } from 'assets/icons/Plus.svg';
import { ReactComponent as PaperPlaneIcon } from 'assets/icons/PaperPlane.svg';
import { ReactComponent as ThoughtsBubble } from 'assets/icons/ThoughtsBubble.svg';
import { ROUTE_ADD_TO_LIST, ROUTE_NICE_FIND, ROUTE_SELECT_CHAT_MODAL } from 'routes';
import useOpenModal from 'helpers/useOpenModal';
import useThoughtsModal from 'helpers/useThoughtsModal';

import { CardSchema, MessageType } from 'constants/graphqlTypes';

import withHapticFeedback from 'helpers/withHapticFeedback';
import { INavigation } from 'providers/helpers/models';
import { NavigationContext } from 'providers/helpers/contexts';
import { useProfile } from 'graphQL/profile/hooks';
import { useGotoMoveToList } from 'helpers/useContextMenu';
import getAuthUser from 'helpers/getAuthUser';
import { ReactionsProvider } from 'components/Reactions/hooks';
import { MenuWrapper } from './styled';
import IconButton from './IconButton';

interface CardFooterMenuProps {
  card: CardSchema;
  color: string;
}

const CardFooterMenu: React.FC<CardFooterMenuProps> = ({ card, color }) => {
  const { userData } = useProfile();
  const { isScrolled } = useContext<INavigation>(NavigationContext);
  const { pickId } = card;
  const { userId } = getAuthUser();

  const pickIdOfLoggedUser = card?.picks?.items.find(({ userId: pickedUserId }) => pickedUserId === userId)?.id;

  const { externalSources, isPickedByMe, isSavedByMe } = card ?? {};
  const cardImage = card?.cardStyle?.thumbnailImageSet?.images?.[0]?.url ?? undefined;
  const cardColorValue = card?.cardStyle?.bgColor?.colorValue;
  const { sourceUrl, sourceDomainName } = externalSources?.items[0] ?? {};

  // Get the first parent card's ID as the listId
  const listId = card?.parentCards?.items?.[0]?.cardId;

  const niceFindLocationVariable = {
    pickId,
    cardId: card.id,
    listId,
    card,
    cardImage,
    cardColorValue,
    sourceUrl: sourceUrl || undefined,
    sourceDomainName: sourceDomainName || undefined,
    userData,
  };

  const userPickSchemaRef = card?.extraData?.items?.find((data) => data?.user?.id === userId)?.id;

  const { toggle: goToMovePick } = useGotoMoveToList({
    pickId: pickIdOfLoggedUser ?? '',
    cardId: card.id ?? '',
    currentListId: card.id ?? '',
    userPickSchemaRef,
    disableListRedirect: true,
  });

  const goToNiceFind = useOpenModal(ROUTE_NICE_FIND, false, niceFindLocationVariable);

  const goToPickFlow = useOpenModal(ROUTE_ADD_TO_LIST, false, {
    pickId,
    cardId: card.id,
    isPickFlow: true,
    listId,
    isSavedPick: isSavedByMe && !isPickedByMe,
  });

  const handlePick = () => {
    if (isPickedByMe) {
      goToMovePick?.();
    } else if (isSavedByMe) {
      goToPickFlow();
    } else {
      goToNiceFind();
    }
  };

  const goToChatSelect = useOpenModal(ROUTE_SELECT_CHAT_MODAL, false, {
    entityId: pickId,
    messageType: MessageType.Pick,
    collectionId: null,
  });

  const openThoughts = useThoughtsModal({
    id: pickId || '',
    openFromCardDetailPage: true,
  });

  return (
    <ReactionsProvider
      value={{
        isQuickReactionOpen: false,
        thoughtId: null,
        pickerId: card.user?.id,
        cardId: card.id,
        user: card.user,
        setThoughtId: () => {},
        setPickerId: () => {},
        setCardId: () => {},
        setUser: () => {},
      }}
    >
      <MenuWrapper>
        <IconButton
          ariaLabel="Add"
          color={color}
          handleClick={(e) => {
            e.stopPropagation();
            withHapticFeedback(() => !isScrolled && handlePick())();
          }}
        >
          <PlusIcon />
        </IconButton>
        <IconButton
          ariaLabel="Thoughts"
          color={color}
          handleClick={(e) => {
            e.stopPropagation();
            if (!isScrolled) {
              openThoughts();
            }
          }}
        >
          <ThoughtsBubble />
        </IconButton>
        <IconButton
          ariaLabel="Send"
          color={color}
          handleClick={(e) => {
            e.stopPropagation();
            if (!isScrolled) {
              goToChatSelect();
            }
          }}
        >
          <PaperPlaneIcon />
        </IconButton>
      </MenuWrapper>
    </ReactionsProvider>
  );
};

export default CardFooterMenu;
